import { Box, Button, ListItemIcon, ListItemText, Menu, MenuItem, Stack, Typography } from "@mui/material";
import MeetingStyles from "./MeetingStyle";
import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { store } from "../../utils/store";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../ui/YhTable";
import { ReactComponent as VerticalDotIcon } from "../../assets/icons/verticalDot.svg";
import EditIcon from "@mui/icons-material/Edit";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import { deleteMeeting, getMeeting } from "./Service/Service";
import urls from "../../global/constants/UrlConstants";
import history from "../../utils/history";
import {
  OrgNameCell,
  useOrgNameMap,
} from "../../Shared/Organization/OrganizationSelect";
import ConfirmationModel from "../../global/components/ConfirmationModal/confirmationModel";
import {
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import PersonIcon from "@mui/icons-material/Person";
import GroupIcon from "@mui/icons-material/Group";
import { selectActiveOrganizationId } from "../../redux/authSlice";

const MeetingList = () => {
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const privileges = store.getState().auth?.privileges || {};
  const [tableData, setTableData] = useState([]);
  const [selectedMeetingId, setSelectedMeetingId] = useState<string | null>(
    null
  );
  const [selectedMeetingLabel, setSelectedMeetingLabel] = useState("");
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [total, setTotal] = useState(0);
  const [scope, setScope] = useState<"mine" | "all">("all");
  const orgNameMap = useOrgNameMap();
  const activeOrganizationId = useSelector(selectActiveOrganizationId) || "";

  const canDelete =
    privileges?.["Meeting List"]?.includes("delete") ||
    privileges?.["Meeting List"]?.includes("full control") ||
    privileges?.["Meeting List"]?.includes("create") ||
    privileges?.["Meeting List"]?.includes("edit");

  const canSeeAll =
    privileges?.["Meeting List"]?.includes("see all") ||
    privileges?.["Meeting List"]?.includes("full control");

  const onClickHandler = () => {
    history.push(urls.createMeetingViewPath);
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Host",
      field: "name",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
          <Typography sx={[MeetingStyles.headerName]}>
            {params.row?.host?.name}
          </Typography>
        </Stack>
      ),
    },
    {
      headerName: "Email",
      field: "calendar",
      minWidth: 200,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <Stack
          direction="column"
          justifyContent="flex-start"
          alignItems="flex-start"
          spacing={1}
        >
          <Typography sx={[MeetingStyles.feildText]}>
            {params.row?.calendar}
          </Typography>
        </Stack>
      ),
    },
    {
      headerName: "Organization",
      field: "organizationId",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => (
        <OrgNameCell
          organizationId={params.row?.organizationId}
          map={orgNameMap}
        />
      ),
    },
    {
      headerName: "Type",
      field: "meetingType",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const type = params.row?.meetingType || "both";
        const label =
          type === "telephonic"
            ? "Telephonic"
            : type === "virtual"
            ? "Virtual"
            : "Both";
        return (
          <Typography sx={[MeetingStyles.feildText]}>{label}</Typography>
        );
      },
    },
    {
      headerName: "Action",
      field: "action",
      minWidth: 100,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <Box sx={MeetingStyles.verticalDotButton}>
          <Button
            className="action-button"
            data-action-button="true"
            onClick={(event) => {
              event.stopPropagation();
              const rect = event.currentTarget.getBoundingClientRect();
              setSelectedMeetingId(params.row._id);
              setSelectedMeetingLabel(
                params.row?.host?.name ||
                  params.row?.calendar ||
                  "this meeting"
              );
              setMenuPosition({ top: rect.bottom, left: rect.left });
            }}
          >
            <VerticalDotIcon />
          </Button>
        </Box>
      ),
    },
  ];

  const confirmDelete = async () => {
    if (!selectedMeetingId) return;
    setDeleting(true);
    try {
      const res = await deleteMeeting(selectedMeetingId);
      if (res?.success === false) {
        openErrorNotification(res?.message || "Failed to delete meeting");
        return;
      }
      openSuccessNotification("Meeting deleted");
      setDeleteOpen(false);
      setSelectedMeetingId(null);
      await handleTableData();
    } catch (e: any) {
      openErrorNotification(e?.message || "Failed to delete meeting");
    } finally {
      setDeleting(false);
    }
  };

  const renderMenu = () => (
    <Menu
      open={Boolean(menuPosition)}
      onClose={() => {
        setMenuPosition(null);
      }}
      anchorReference="anchorPosition"
      anchorPosition={
        menuPosition
          ? { top: menuPosition.top, left: menuPosition.left - 190 }
          : undefined
      }
      PaperProps={{ sx: { width: 220, maxWidth: "100%" } }}
      sx={MeetingStyles.menuItem}
    >
      <MenuItem style={{ cursor: "default" }}>
        <ListItemText>Actions</ListItemText>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setMenuPosition(null);
          history.push({
            pathname: urls.createMeetingViewPath,
            state: {
              meetingId: selectedMeetingId,
              mode: "edit",
            },
          });
        }}
      >
        <ListItemIcon>
          <EditIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>Edit</ListItemText>
      </MenuItem>
      {canDelete ? (
        <MenuItem
          onClick={() => {
            setMenuPosition(null);
            setDeleteOpen(true);
          }}
        >
          <ListItemIcon>
            <DeleteOutlineIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      ) : null}
    </Menu>
  );

  useEffect(() => {
    handleTableData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize, scope, activeOrganizationId]);

  const handleTableData = async () => {
    try {
      setLoading(true);
      const orgId =
        activeOrganizationId &&
        activeOrganizationId !== "all" &&
        activeOrganizationId !== "*"
          ? String(activeOrganizationId)
          : undefined;
      const response = await getMeeting({
        userId: store.getState().auth.userId,
        pageIndex: page,
        pageSize,
        scope,
        organizationId: orgId,
      });
      setTableData(response?.data || []);
      setTotal(response?.total || 0);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
            Meeting List
          </h1>
          {canSeeAll && (
            <div className="flex items-center rounded-lg border border-gray-200 bg-white p-0.5">
              <button
                type="button"
                onClick={() => { setScope("all"); setPage(0); }}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  scope === "all"
                    ? "bg-[#A02448] text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <GroupIcon sx={{ fontSize: 16 }} />
                All Users
              </button>
              <button
                type="button"
                onClick={() => { setScope("mine"); setPage(0); }}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-semibold transition ${
                  scope === "mine"
                    ? "bg-[#A02448] text-white shadow-sm"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <PersonIcon sx={{ fontSize: 16 }} />
                My Meetings
              </button>
            </div>
          )}
        </div>
        {privileges &&
          (privileges?.["Meeting List"]?.includes("create") ||
            privileges?.["Meeting List"]?.includes("full control")) ? (
          <button
            type="button"
            onClick={() => onClickHandler()}
            className="inline-flex h-10 shrink-0 items-center gap-1 rounded-xl bg-[#A02448] px-4 text-sm font-semibold text-white shadow-sm transition hover:bg-[#841C39]"
          >
            + Add Meeting
          </button>
        ) : null}
      </div>

      <div className="min-h-0 flex-1 overflow-auto">
        <YhTable
          columns={columns}
          rows={tableData}
          getRowId={(row) => row._id}
          rowHeight={90}
          loading={loading}
          emptyMessage={scope === "all" ? "No meetings found" : "No meetings found for you"}
          className="min-h-[320px]"
          page={page}
          pageSize={pageSize}
          totalRows={total}
          onPageChange={setPage}
          onPageSizeChange={(s) => {
            setPageSize(s);
            setPage(0);
          }}
        />
        {renderMenu()}
      </div>

      <ConfirmationModel
        open={deleteOpen}
        title="Delete meeting"
        description={`Delete meeting for ${
          selectedMeetingLabel || "this host"
        }? It will be removed from the list. You can create a new meeting for this host later.`}
        confirmButtonText={deleting ? "Deleting…" : "Delete"}
        cancelButtonText="Cancel"
        onConfirm={confirmDelete}
        onCancel={() => {
          if (deleting) return;
          setDeleteOpen(false);
        }}
      />
    </div>
  );
};

export default MeetingList;
